import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useLoginCheck from "../util/useLoginCheck";
import Spinner from "../util/Spinner";
import { fetchRestaurantList } from "../slice/restaurantListsSlice";
import {
  fetchRestaurantListItems,
  setPage,
} from "../slice/restaurantListItemsSlice";
import { fetchRatings } from "../slice/ratingSlice";
import RestaurantListItemRow, {
  RestaurantListItemCard,
} from "./RestaurantListItemRow";

const RestaurantList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { sid } = useParams();

  const { restaurantList, isLoading } = useSelector(
    (state) => state.restaurantLists
  );
  const { content, page, totalPages, totalElements, isLoadingItems } =
    useSelector((state) => state.restaurantListItems);
  const [shouldRefresh, setShouldRefresh] = useState(true);

  const navigate = useNavigate();

  useLoginCheck();

  useEffect(() => {
    dispatch(fetchRatings());
  }, [dispatch]);

  useEffect(() => {
    if (sid) {
      dispatch(fetchRestaurantList(sid));
    }
  }, [dispatch, sid, shouldRefresh]);

  useEffect(() => {
    if (sid) {
      dispatch(fetchRestaurantListItems({ sid, page }));
    }
  }, [dispatch, sid, page, shouldRefresh]);

  const addRestaurant = () => {
    navigate(`${location.pathname}/add`);
  };

  const handleNext = () => {
    if (page < totalPages - 1) {
      dispatch(setPage(page + 1));
    }
  };

  const handlePrevious = () => {
    if (page > 0) {
      dispatch(setPage(page - 1));
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      {isLoading ? (
        <Spinner />
      ) : (
        <div>
          {restaurantList ? (
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold text-stone-800">
                  {restaurantList?.name}
                </div>
                <div className="text-xs font-semibold text-stone-500">
                  {restaurantList?.type}
                </div>
                <div className="text-xs font-semibold text-teal-700">
                  {totalElements} restaurants
                </div>
              </div>
              <button className="btn-primary shrink-0" onClick={addRestaurant}>
                Add
              </button>
            </div>
          ) : (
            <p className="text-stone-500">No restaurant list entered</p>
          )}
        </div>
      )}
      {isLoadingItems ? (
        <Spinner />
      ) : (
        <>
          {content?.length > 0 ? (
            <div className="mt-4">
              <table className="hidden md:table w-full card">
                <tbody className="divide-y divide-stone-200">
                  {content?.map((item) => (
                    <RestaurantListItemRow
                      key={item?.sid}
                      restaurantListItem={item}
                    />
                  ))}
                </tbody>
              </table>
              <ul className="md:hidden space-y-3">
                {content?.map((item) => (
                  <RestaurantListItemCard
                    key={item?.sid}
                    restaurantListItem={item}
                  />
                ))}
              </ul>

              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={handlePrevious}
                  disabled={page === 0}
                  className="btn-secondary min-h-11 px-4"
                >
                  {"<"}
                </button>
                <span className="text-sm text-stone-600">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={handleNext}
                  disabled={page >= totalPages - 1}
                  className="btn-secondary min-h-11 px-4"
                >
                  {">"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-stone-500 mt-4">
              No restaurants on your list!
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default RestaurantList;
